import { AbstractControl } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';

// ─────────────────────────────────────────────────────────────────────────────
// Validators Registry
//
// Named validator expressions shared across field config definitions.
// Centralising them here:
//   - eliminates duplicate inline functions (e.g. notEmpty, validRegex)
//   - makes them independently testable
//   - provides a stable named reference if definitions are ever serialised
// ─────────────────────────────────────────────────────────────────────────────

export const FIELD_VALIDATORS = {
  /**
   * Rejects blank / whitespace-only strings.
   * Used on: label
   */
  notEmpty: (control: AbstractControl): boolean => control.value?.trim()?.length > 0,

  /**
   * Accepts empty values (field is optional) or valid regex patterns.
   * Used on: pattern (text, tel)
   */
  validRegex: (control: AbstractControl): boolean => {
    if (!control.value) return true;
    try {
      new RegExp(control.value);
      return true;
    } catch {
      return false;
    }
  },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Common Field Configs
//
// Defined as a map (object) rather than an array to enable:
//   - Selective inclusion (field types pick which common fields they need)
//   - Custom ordering (array order determines UI order)
//   - Deep overrides via deepMergeFieldConfig()
//
// Not all common fields apply to all field types. For example:
//   - `placeholder`: only for text-based inputs (text, email, tel, textarea)
//   - `required`: most fields, but not text-label (display-only)
//   - `pattern`: only for text and tel fields
//
// Naming convention:
//   - These FormlyFieldConfig entries describe the CONFIG EDITOR form
//   - Their `key` values correspond to FieldDefinition properties
// ─────────────────────────────────────────────────────────────────────────────

export const COMMON_FIELD_CONFIGS: Record<string, FormlyFieldConfig> = {
  type: {
    key: 'type',
    type: 'field-type-selector',
    className: 'flex-basis-full', // Full width to accommodate the custom selector UI
    props: {
      // isOpen controls accordion expansion (true for new fields)
      isOpen: false,
    },
  },

  label: {
    key: 'label',
    type: 'text',
    className: 'flex-basis-full', // Full width for better readability
    props: {
      label: 'formBuilder.label',
      placeholder: 'formBuilder.labelPlaceholder',
      required: true,
    },
    validators: {
      notEmpty: {
        expression: FIELD_VALIDATORS.notEmpty,
        message: 'formBuilder.validation.labelRequired',
      },
    },
  },

  placeholder: {
    key: 'placeholder',
    type: 'text',
    props: {
      label: 'formBuilder.placeholder',
      placeholder: 'formBuilder.placeholderPlaceholder',
    },
  },

  required: {
    key: 'required',
    type: 'checkbox',
    props: {
      label: 'formBuilder.requiredField',
    },
  },

  allowMultiple: {
    key: 'allowMultiple',
    type: 'checkbox',
    props: {
      label: 'formBuilder.allowMultiple',
    },
  },

  /**
   * Pattern field for text-based inputs that support regex validation (text, tel).
   * Uses the shared validRegex validator — accepts empty or valid regex strings.
   */
  pattern: {
    key: 'pattern',
    type: 'text',
    props: {
      label: 'formBuilder.pattern',
      placeholder: 'formBuilder.patternPlaceholder',
      description: 'formBuilder.patternHelp',
    },
    validators: {
      validRegex: {
        expression: FIELD_VALIDATORS.validRegex,
        message: 'formBuilder.validation.invalidRegexPattern',
      },
    },
  },

  /**
   * Width control: lets the user choose how wide a field appears in the rendered form.
   * 'auto'  → field takes its natural share of the row (default flex behaviour)
   * 'full'  → field always spans the entire row (maps to className: 'flex-basis-full')
   */
  width: {
    key: 'width',
    type: 'select',
    props: {
      label: 'formBuilder.fieldWidth',
      options: [
        { value: 'auto', label: 'formBuilder.fieldWidth.auto' },
        { value: 'full', label: 'formBuilder.fieldWidth.full' },
      ],
    },
  },

  /**
   * Per-field pre-fill control.
   * 'default' (or undefined) → inherit the form-level default.
   * 'always'  → always pre-fill this field from the last submission.
   * 'never'   → never pre-fill this field, even if the form default is on.
   *
   * Uses a select (not a checkbox) so the three-way intent is always explicit.
   */
  prefillLastValue: {
    key: 'prefillLastValue',
    type: 'select',
    defaultValue: 'default',
    props: {
      label: 'formBuilder.prefillLastValue',
      description: 'formBuilder.prefillLastValueDescription',
      options: [
        { value: 'default', label: 'formBuilder.prefillLastValue.default' },
        { value: 'always', label: 'formBuilder.prefillLastValue.always' },
        { value: 'never', label: 'formBuilder.prefillLastValue.never' },
      ],
    },
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Options Editor Config (shared by radio, checkbox-group, and lookup-list editor)
//
// Static constant — no per-instance state required. All option-bearing fields
// and the lookup-list edit page share the identical options editor structure.
//
// Each option row contains:
//   - label (required) — displayed as the option text
//   - value (required) — stored value when option is selected
//   - description (optional) — smaller-font supplementary text beneath the label
//   - isDefault (optional) — marks one option as the pre-selected default
//
// All content is user-generated, so no translation keys are used for option
// text — only for UI control labels (placeholders, checkbox label).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Shared `fieldArray` config for the `options-editor` Formly type.
 *
 * Exported so it can be re-used by both `OPTIONS_EDITOR_FIELD` (inline options
 * in the form builder) and `lookup-list-edit.component.ts` (standalone lookup
 * list editing page) — keeping the two identical and avoiding duplication.
 */
export const OPTIONS_EDITOR_FIELDARRAY: FormlyFieldConfig = {
  fieldGroup: [
    {
      key: 'label',
      type: 'text',
      props: {
        placeholder: 'formBuilder.optionEditorLabelPlaceholder',
        required: true,
      },
    },
    {
      key: 'value',
      type: 'text',
      props: {
        placeholder: 'formBuilder.optionEditorValuePlaceholder',
        required: true,
      },
    },
    {
      key: 'metricAmount',
      type: 'number',
      props: {
        placeholder: 'formBuilder.optionEditorMetricPlaceholder',
      },
    },
    {
      key: 'description',
      type: 'text',
      props: {
        placeholder: 'formBuilder.optionEditorDescriptionPlaceholder',
      },
    },
    {
      key: 'isDefault',
      type: 'checkbox',
      props: {
        label: 'formBuilder.optionIsDefault',
      },
    },
  ],
};

export const OPTIONS_EDITOR_FIELD: FormlyFieldConfig = {
  key: 'options',
  type: 'options-editor',
  className: 'flex-basis-full',
  // For FieldArrayType fields, `model` refers to the array value itself — not the parent
  // form model. Use `field.parent.model` to read sibling fields like `optionsSource`.
  hideExpression: (_model: any, _formState: any, field?: FormlyFieldConfig) =>
    (field?.parent?.model as any)?.optionsSource === 'lookup-list',
  fieldArray: OPTIONS_EDITOR_FIELDARRAY,
};

// ─────────────────────────────────────────────────────────────────────────────
// Options Source Fields
//
// Used by option-bearing fields (select, dropdown, radio, checkbox-group) to
// allow the user to choose between defining options inline or pulling them
// from a shared, reusable lookup list.
// ─────────────────────────────────────────────────────────────────────────────

/** Toggle between 'inline' options and a reusable 'lookup-list'. */
export const OPTIONS_SOURCE_TOGGLE_FIELD: FormlyFieldConfig = {
  key: 'optionsSource',
  type: 'select',
  className: 'flex-basis-full',
  defaultValue: 'inline',
  props: {
    label: 'formBuilder.optionsSource',
    options: [
      { value: 'inline', label: 'formBuilder.optionsSource.inline' },
      { value: 'lookup-list', label: 'formBuilder.optionsSource.lookupList' },
    ],
  },
};

/**
 * Selector for a shared lookup list — shown only when optionsSource === 'lookup-list'.
 *
 * Options are populated dynamically via formState.lookupListOptions, which is set
 * by GenericFieldConfigEditorComponent from LookupListService.
 *
 * Uses the collapsible-radio-list Formly type so the options are displayed in a
 * collapsible accordion with a responsive column grid.
 */
export const LOOKUP_LIST_ID_FIELD: FormlyFieldConfig = {
  key: 'lookupListId',
  type: 'collapsible-radio-list',
  className: 'flex-basis-full',
  props: {
    label: 'formBuilder.lookupList-emptylabel',
    selectedLabelPrefix: 'formBuilder.lookupList-selectedList-prefix',
    options: [],
    columns: 2,
  },
  expressionProperties: {
    'props.options': 'formState.lookupListOptions',
  },
  hideExpression: (model: any) => model?.optionsSource !== 'lookup-list',
};

// ─────────────────────────────────────────────────────────────────────────────
// Advanced Section Builder
//
// Most field types wrap their advanced properties in an identical collapsible
// section. This helper avoids repeating the wrapper boilerplate across every
// field type definition.
// ─────────────────────────────────────────────────────────────────────────────

export function advancedSection(...fields: FormlyFieldConfig[]): FormlyFieldConfig {
  return {
    className: 'flex-basis-full',
    wrappers: ['collapsible-section'],
    props: {
      collapsibleHeader: 'formBuilder.advancedProperties',
      collapsibleIsOpen: false,
      collapsibleTestId: 'field-config-advanced-toggle',
    },
    fieldGroup: fields,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Deep Merge Utility
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Deep merge utility for customizing FormlyFieldConfig objects.
 * Allows field-type definitions to override nested properties while inheriting the rest.
 *
 * Example:
 *   deepMergeFieldConfig(COMMON_FIELD_CONFIGS['placeholder'], {
 *     props: { placeholder: 'Custom hint text' }
 *   })
 *   // Result: keeps key, type, props.label, but overrides props.placeholder
 */
export function deepMergeFieldConfig(
  base: FormlyFieldConfig,
  overrides: Partial<FormlyFieldConfig>,
): FormlyFieldConfig {
  const result = { ...base };

  for (const key of Object.keys(overrides) as Array<keyof FormlyFieldConfig>) {
    const value = overrides[key];
    if (value !== undefined) {
      if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
        // Deep merge for nested objects (props, validators, etc.)
        result[key] = { ...result[key], ...value } as any;
      } else {
        // Shallow copy for primitives and arrays
        result[key] = value as any;
      }
    }
  }

  return result;
}
